import { useState } from "react";

import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { Pill } from "../../components/Pill";
import { type ApiJob, cancelJob, retryJob, useJobs } from "../../data/api/jobs";
import { relTime } from "../../lib/time";

// Operator-only queue view (Settings → Jobs). Rendered only for staff; the
// server also gates the API, so a non-staff viewer never sees this section.
// See specifications/background-jobs.md.

const STATE_COLOR: Record<string, string> = {
  cancelled: "#8593a0",
  dead: "#c2382d",
  failed: "#e0703a",
  queued: "#5b7fb0",
  running: "#9a6a0d",
  succeeded: "#2c8452",
};

const FILTERS = ["all", "running", "queued", "failed", "dead"] as const;
type Filter = (typeof FILTERS)[number];

const ms = (iso: string | null): number => (iso ? Date.parse(iso) : Date.now());
const canCancel = (s: string): boolean => s === "queued" || s === "running";
const canRetry = (s: string): boolean =>
  s === "failed" || s === "dead" || s === "cancelled";

export const Jobs = ({ staff }: { readonly staff: boolean }) => {
  const [filter, setFilter] = useState<Filter>("all");
  const [busy, setBusy] = useState<string | null>(null);
  const { counts, error, jobs, loading, refetch } = useJobs(
    staff,
    filter === "all" ? undefined : filter,
  );

  if (!staff || error) {
    return null;
  }

  const act = (fn: () => Promise<unknown>, id: string): void => {
    setBusy(id);
    void fn()
      .then(refetch)
      .catch(() => undefined)
      .finally(() => setBusy(null));
  };

  // Collapse the per-(kind,state) counts into a compact summary, most-active
  // states first.
  const summary = [...counts]
    .filter((c) => c.n > 0)
    .sort((a, b) => b.n - a.n)
    .slice(0, 8);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>Jobs queue</h3>
        <Button onClick={refetch} size="sm" variant="ghost">
          Refresh
        </Button>
      </div>

      <div className={styles.card}>
        <div className={styles.row}>
          <div
            className={styles.grow}
            style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
          >
            {summary.length === 0 ? (
              <span className={styles.help}>Queue is empty.</span>
            ) : (
              summary.map((c) => (
                <Pill
                  dotColor={STATE_COLOR[c.state]}
                  key={`${c.kind}:${c.state}`}
                >
                  {c.kind} · {c.state} · {c.n}
                </Pill>
              ))
            )}
          </div>
        </div>

        <div className={styles.row} style={{ gap: 6 }}>
          {FILTERS.map((f) => (
            <Button
              key={f}
              onClick={() => setFilter(f)}
              size="sm"
              variant={filter === f ? "accent" : "ghost"}
            >
              {f}
            </Button>
          ))}
        </div>

        {loading && jobs.length === 0 ? (
          <div className={styles.row}>
            <span className={styles.help}>Loading jobs…</span>
          </div>
        ) : jobs.length === 0 ? (
          <div className={styles.row}>
            <span className={styles.help}>No jobs in this state.</span>
          </div>
        ) : (
          jobs.map((j) => (
            <JobRow
              busy={busy === j.public_id}
              job={j}
              key={j.public_id}
              onAct={act}
            />
          ))
        )}
      </div>
    </section>
  );
};

const JobRow = ({
  busy,
  job,
  onAct,
}: {
  readonly busy: boolean;
  readonly job: ApiJob;
  readonly onAct: (fn: () => Promise<unknown>, id: string) => void;
}) => (
  <div className={styles.row}>
    <Pill dotColor={STATE_COLOR[job.state]}>{job.state}</Pill>
    <div className={styles.grow}>
      <div className={styles.label}>
        {job.kind}
        <span className={styles.help} style={{ marginLeft: 8 }}>
          attempt {job.attempts}/{job.max_attempts}
        </span>
      </div>
      <div className={styles.help}>
        {relTime(ms(job.finished_at ?? job.last_attempt_at ?? job.created_at))}
        {job.last_error ? ` · ${job.last_error.slice(0, 120)}` : ""}
      </div>
    </div>
    {canCancel(job.state) ? (
      <Button
        disabled={busy}
        onClick={() => onAct(() => cancelJob(job.public_id), job.public_id)}
        size="sm"
        variant="ghost"
      >
        Cancel
      </Button>
    ) : null}
    {canRetry(job.state) ? (
      <Button
        disabled={busy}
        onClick={() => onAct(() => retryJob(job.public_id), job.public_id)}
        size="sm"
        variant="accent"
      >
        Retry
      </Button>
    ) : null}
  </div>
);
